// The boat's cameras, as they draw (TOM-797, cloud ADR 0411): the tile grid,
// the "Seen by Neomaris since connected" event list, a snapshot large, and the
// alarm screens' snapshot button. Pure views over lib/cameras.ts's words, so
// they render the same under a test; the page (pages/Cameras.tsx) owns the
// reads and the live player (CameraPlayer.tsx).
//
// Every picture is a camera's, so it goes through Night's tint once
// (CAMERA_MEDIA_STYLE with styles/cameras.css), and its box is reserved from
// the snapshot's stored size before its bytes arrive.

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import {
  CAMERA_EVENT_FILTERS,
  CAMERA_EVENTS_LABEL,
  CAMERA_MEDIA_STYLE,
  CAMERAS_EMPTY,
  CAMERA_NO_STREAM,
  cameraEventWhat,
  cameraHasStream,
  cameraSnapshotUrl,
  formatCameraWhen,
  personSeenLine,
  shownCameraEvents,
  type Camera,
  type CameraEvent,
  type CameraEventFilter,
  type CameraEventType,
  type CameraSnapshotRef,
} from '../../lib/cameras';
import {
  useCameraAlarmSnapshots,
  type CameraEventsState,
  type CamerasState,
} from '../../lib/useCameras';
import { Icon } from '../ui/Icon';

/** A snapshot to show large: its ref and what it is of. */
export interface CameraShot {
  snapshot: CameraSnapshotRef;
  title: string;
}

function ratioStyle(snapshot: CameraSnapshotRef | null): CSSProperties {
  const w = snapshot?.width ?? 0;
  const h = snapshot?.height ?? 0;
  return { aspectRatio: w > 0 && h > 0 ? `${w} / ${h}` : '16 / 9' };
}

/** One camera's picture, its box reserved from the ref. */
export function CameraSnapshotImage({
  boatId,
  snapshot,
  alt,
  className,
  lazy = true,
}: {
  boatId: string;
  snapshot: CameraSnapshotRef;
  alt: string;
  className?: string;
  lazy?: boolean;
}) {
  return (
    <img
      className={`cam-media${className === undefined ? '' : ` ${className}`}`}
      src={cameraSnapshotUrl(boatId, snapshot.snapshotId)}
      alt={alt}
      width={snapshot.width}
      height={snapshot.height}
      style={CAMERA_MEDIA_STYLE}
      decoding="async"
      loading={lazy ? 'lazy' : undefined}
      draggable={false}
    />
  );
}

export function CameraTile({
  boatId,
  camera,
  nowMs,
  timeZone,
  onOpen,
  live,
}: {
  boatId: string;
  camera: Camera;
  nowMs: number;
  timeZone?: string;
  onOpen?: (camera: Camera) => void;
  /** The live grid's layer over the still (TOM-797), while the page plays it. */
  live?: ReactNode;
}) {
  const snap = camera.latestSnapshot;
  const person = personSeenLine(camera, nowMs);
  // A camera that shares no stream offers no player: its tile is not a button.
  const streams = cameraHasStream(camera);
  const tileClass = `cam-tile__button${camera.online ? '' : ' is-offline'}${streams ? '' : ' is-static'}`;
  const body = (
    <>
      <span className="cam-tile__frame" style={ratioStyle(snap)}>
        {snap !== null ? (
          <CameraSnapshotImage
            boatId={boatId}
            snapshot={snap}
            alt={`A picture from ${camera.name}, ${formatCameraWhen(snap.takenAt, timeZone)}`}
            className="cam-tile__img"
          />
        ) : (
          <span className="cam-tile__none">No recent picture</span>
        )}
        {live}
        {person !== null ? (
          <span className="cam-tile__person" data-testid="camera-person">
            <span className="cam-dot" aria-hidden="true" />
            {person}
          </span>
        ) : null}
      </span>
      <span className="cam-tile__body">
        <span className="cam-tile__name">{camera.name}</span>
        <span
          className={`tb-chip ${camera.online ? 'tb-chip--good' : 'tb-chip--bad'}`}
          data-testid="camera-online"
        >
          {camera.online ? 'Online' : 'Offline'}
        </span>
      </span>
      {/* The boat takes a picture only when a person or a sound starts, so a
            still can be old: always say when it was taken (TOM-825). */}
      {snap !== null ? (
        <span className="cam-tile__meta" data-testid="camera-picture-time">
          Picture from {formatCameraWhen(snap.takenAt, timeZone)}
        </span>
      ) : null}
      {streams ? null : (
        <span className="cam-tile__meta" data-testid="camera-no-stream">
          {CAMERA_NO_STREAM}
        </span>
      )}
    </>
  );
  return (
    <li className="cam-tile" data-testid={`camera-tile-${camera.cameraId}`}>
      {/* A camera that shares no stream opens a pictures-only sheet: its
          latest picture and "Take a picture now" (TOM-838). */}
      <button
        type="button"
        className={tileClass}
        aria-haspopup="dialog"
        aria-label={
          streams ? `${camera.name}: open the camera` : `${camera.name}: open its pictures`
        }
        onClick={() => onOpen?.(camera)}
      >
        {body}
      </button>
    </li>
  );
}

export function CameraEventRow({
  boatId,
  event,
  timeZone,
  onOpenSnapshot,
}: {
  boatId: string;
  event: CameraEvent;
  timeZone?: string;
  onOpenSnapshot?: (shot: CameraShot) => void;
}) {
  const what = cameraEventWhat(event, timeZone);
  const gap = event.type === 'gap';
  return (
    <li
      className={`cam-event${gap ? ' cam-event--gap' : ''}`}
      data-testid={`camera-event-${event.eventId}`}
    >
      <time className="cam-event__time" dateTime={event.start}>
        {formatCameraWhen(event.start, timeZone)}
      </time>
      <span className="cam-event__body">
        <span className="cam-event__what">{what}</span>
        {!gap && event.cameraName !== null ? (
          <span className="cam-event__camera">{event.cameraName}</span>
        ) : null}
      </span>
      {event.snapshot !== null ? (
        <button
          type="button"
          className="cam-event__thumb"
          aria-haspopup="dialog"
          aria-label={`View the picture of ${what.toLowerCase()} larger`}
          onClick={() =>
            event.snapshot !== null &&
            onOpenSnapshot?.({
              snapshot: event.snapshot,
              title: `${what}${event.cameraName !== null ? ` · ${event.cameraName}` : ''}`,
            })
          }
        >
          <CameraSnapshotImage boatId={boatId} snapshot={event.snapshot} alt="" />
        </button>
      ) : null}
    </li>
  );
}

export interface CamerasViewProps {
  boatId: string;
  state: CamerasState;
  events: CameraEventsState;
  filter: CameraEventFilter;
  nowMs: number;
  timeZone?: string;
  onRetry: () => void;
  onRetryEvents: () => void;
  onFilter: (filter: CameraEventFilter) => void;
  onLoadMore: () => void;
  onOpenCamera: (camera: Camera) => void;
  onOpenSnapshot: (shot: CameraShot) => void;
  /** Each tile's live layer, or null for a still (TOM-797). */
  renderTileLive?: (camera: Camera) => ReactNode;
  /** "Uses about X Mbit/s of boat upload" for the whole grid, or null. */
  gridUpload?: string | null;
}

function EventList({
  boatId,
  cameras,
  events,
  filter,
  timeZone,
  onFilter,
  onLoadMore,
  onRetryEvents,
  onOpenSnapshot,
}: Pick<
  CamerasViewProps,
  | 'boatId'
  | 'events'
  | 'filter'
  | 'timeZone'
  | 'onFilter'
  | 'onLoadMore'
  | 'onRetryEvents'
  | 'onOpenSnapshot'
> & { cameras: readonly Camera[] }) {
  const label = events.kind === 'ready' ? events.label : CAMERA_EVENTS_LABEL;
  const shown = events.kind === 'ready' ? shownCameraEvents(events.events) : [];
  const toggle = (type: CameraEventType) => {
    const types = filter.types.includes(type)
      ? filter.types.filter((t) => t !== type)
      : [...filter.types, type];
    onFilter({ ...filter, types });
  };
  return (
    <section className="cam-events tb-card" aria-labelledby="cam-events-title">
      <h2 className="tb-card__header" id="cam-events-title">
        {label}
      </h2>
      <div className="cam-events__filters">
        <select
          className="tb-select cam-events__camera"
          aria-label="Camera"
          value={filter.cameraId ?? ''}
          onChange={(e) =>
            onFilter({ ...filter, cameraId: e.target.value === '' ? null : e.target.value })
          }
        >
          <option value="">All cameras</option>
          {cameras.map((c) => (
            <option key={c.cameraId} value={c.cameraId}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="cam-events__types" role="group" aria-label="What was seen">
          {CAMERA_EVENT_FILTERS.map((f) => {
            const on = filter.types.includes(f.type);
            return (
              <button
                key={f.type}
                type="button"
                className={`tb-chip cam-filter${on ? ' tb-chip--accent is-on' : ''}`}
                aria-pressed={on}
                onClick={() => toggle(f.type)}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>
      {events.kind === 'loading' ? (
        <p className="muted cam-events__note" aria-busy="true">
          Loading what was seen…
        </p>
      ) : null}
      {events.kind === 'error' ? (
        <div className="cam-events__note" role="status">
          <p className="muted">Couldn’t load the events.</p>
          <button type="button" className="tb-btn tb-btn--gray tb-btn--sm" onClick={onRetryEvents}>
            Try again
          </button>
        </div>
      ) : null}
      {events.kind === 'ready' && shown.length === 0 ? (
        <p className="muted cam-events__note" data-testid="camera-events-empty">
          {filter.cameraId === null && filter.types.length === 0
            ? 'Nothing seen yet.'
            : 'Nothing seen that matches.'}
        </p>
      ) : null}
      {events.kind === 'ready' && shown.length > 0 ? (
        <ul className="cam-events__list" data-testid="camera-events">
          {shown.map((e) => (
            <CameraEventRow
              key={e.eventId}
              boatId={boatId}
              event={e}
              timeZone={timeZone}
              onOpenSnapshot={onOpenSnapshot}
            />
          ))}
        </ul>
      ) : null}
      {events.kind === 'ready' && events.nextBefore !== null ? (
        <div className="cam-events__more">
          <button
            type="button"
            className="tb-btn tb-btn--gray tb-btn--md"
            disabled={events.loadingMore}
            onClick={onLoadMore}
          >
            {events.loadingMore ? 'Loading…' : 'Load more'}
          </button>
          {events.moreFailed ? <p className="muted">Couldn’t load more. Try again.</p> : null}
        </div>
      ) : null}
    </section>
  );
}

export function CamerasView(props: CamerasViewProps) {
  const { boatId, state, nowMs, timeZone, onRetry, onOpenCamera, renderTileLive, gridUpload } =
    props;
  return (
    <section className="cameras-page" data-testid="cameras">
      <div className="page-head">
        <h1>Cameras</h1>
      </div>
      {state.kind === 'loading' ? (
        <p className="muted" aria-busy="true">
          Loading the cameras…
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <div className="tb-card">
          <div className="tb-empty" data-testid="cameras-error">
            <h3 className="tb-empty__title">Couldn’t load the cameras</h3>
            <p className="tb-subhead">Check the connection, then try again.</p>
            <button type="button" className="tb-btn tb-btn--gray tb-btn--md" onClick={onRetry}>
              Try again
            </button>
          </div>
        </div>
      ) : null}
      {state.kind === 'ready' && state.data.cameras.length === 0 ? (
        <div className="tb-card">
          <div className="tb-empty" data-testid="cameras-empty">
            <span className="tb-empty__icon" aria-hidden="true">
              <CameraGlyph />
            </span>
            <p className="tb-empty__title">{CAMERAS_EMPTY}</p>
          </div>
        </div>
      ) : null}
      {state.kind === 'ready' && state.data.cameras.length > 0 ? (
        <>
          {gridUpload ? (
            <p className="cam-grid__upload" data-testid="camera-grid-upload">
              {gridUpload}
            </p>
          ) : null}
          <ul className="cam-grid" data-testid="camera-grid">
            {state.data.cameras.map((c) => (
              <CameraTile
                key={c.cameraId}
                boatId={boatId}
                camera={c}
                nowMs={nowMs}
                timeZone={timeZone}
                onOpen={onOpenCamera}
                live={renderTileLive?.(c) ?? null}
              />
            ))}
          </ul>
          <EventList {...props} cameras={state.data.cameras} />
        </>
      ) : null}
    </section>
  );
}

function CameraGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={26}
      height={26}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
    >
      <path d="m16 13 5.2 3.5a.5.5 0 0 0 .8-.4V7.9a.5.5 0 0 0-.8-.4L16 11" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </svg>
  );
}

// ----- Dialogs ---------------------------------------------------------------

/**
 * A camera dialog over the page: Escape, the close button or a tap outside
 * closes it, and the focus goes back where it was.
 */
export function CameraDialog({
  title,
  onClose,
  children,
  testId,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}): ReactNode {
  const ref = useRef<HTMLDialogElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialog.open) dialog.showModal();
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (dialog.open) dialog.close();
      opener?.focus();
    };
  }, []);
  return createPortal(
    <dialog
      ref={ref}
      className="cam-dialog"
      aria-labelledby={titleId}
      data-testid={testId}
      onKeyDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      // No onClose: the effect's cleanup closes the dialog, and under React's
      // StrictMode that runs once on mount, so a close handler would shut the
      // dialog the moment it opened. Escape, the backdrop and the close
      // button each call onClose themselves.
    >
      <section className="cam-dialog__panel">
        <header className="cam-dialog__head">
          <h2 id={titleId} className="cam-dialog__title">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="cam-dialog__close"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="x" size={18} />
          </button>
        </header>
        {children}
      </section>
    </dialog>,
    document.body
  );
}

/** A snapshot large. */
export function CameraShotDialog({
  boatId,
  shot,
  timeZone,
  onClose,
}: {
  boatId: string;
  shot: CameraShot;
  timeZone?: string;
  onClose: () => void;
}) {
  return (
    <CameraDialog title={shot.title} onClose={onClose} testId="camera-shot-dialog">
      <div className="cam-dialog__media" style={ratioStyle(shot.snapshot)}>
        <CameraSnapshotImage
          boatId={boatId}
          snapshot={shot.snapshot}
          alt={shot.title}
          lazy={false}
        />
      </div>
      <p className="cam-dialog__caption">{formatCameraWhen(shot.snapshot.takenAt, timeZone)}</p>
    </CameraDialog>
  );
}

/**
 * A camera alarm's snapshot beside its evidence (TOM-797): a thumbnail that
 * opens large. Nothing at all without a snapshot.
 */
export function CameraAlarmSnapshot({
  boatId,
  snapshot,
  title,
}: {
  boatId: string;
  snapshot: CameraSnapshotRef | undefined | null;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  if (snapshot === undefined || snapshot === null) return null;
  return (
    <>
      <button
        type="button"
        className="cam-alarm-shot"
        aria-haspopup="dialog"
        aria-label={`View the camera’s picture for ${title} larger`}
        data-testid="camera-alarm-snapshot"
        onClick={() => setOpen(true)}
      >
        <CameraSnapshotImage boatId={boatId} snapshot={snapshot} alt="" />
      </button>
      {open ? (
        <CameraShotDialog
          boatId={boatId}
          shot={{ snapshot, title }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/** One alarm's camera snapshot, read for it alone: the alarm detail page's. */
export function CameraAlarmSnapshotFor({
  boatId,
  alarmId,
  path,
  at,
  title,
}: {
  boatId: string;
  alarmId: string;
  path: string | null;
  at: string | null;
  title: string;
}) {
  const requests = useMemo(
    () => (path === null || at === null ? [] : [{ alarmId, path, at }]),
    [alarmId, path, at]
  );
  const found = useCameraAlarmSnapshots(boatId, requests);
  const snapshot = found.get(alarmId);
  if (snapshot === undefined) return null;
  return (
    <div className="cam-alarm-detail" data-testid="alarm-detail-camera">
      <CameraAlarmSnapshot boatId={boatId} snapshot={snapshot} title={title} />
    </div>
  );
}
